import { GameRouteService } from "../services/GameRouteService";
import { css, htmlArray } from "../helpers/webComponents";
import { GameState } from "@shared/types";

const styles: string = css`
    .content {
        background-color: rgb(15, 77, 163);
        display: flex;
    }

    button {
        width: 30px;
        height: 30px;
        background-color:rgb(15, 77, 163);
        color: white;
        font-size: 18px;
        cursor: pointer;
        border-radius: 5px;
    }

    button:hover {
        background-color: rgb(15, 77, 163);
    }

    #clear, #enter, #back {
        width: 80px;
        grid-column: span 2;
    }

    .hidden {
        display: none;
    }
`;

/**
 * Represents the Keypad page
 */
export class KeypadComponent extends HTMLElement {
    /** Instance of the game event service */

    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /**
     * Stores the current entered code digits.
     */
    private enterdCode: string = "";

    /**
     * Maximum length of the code that can be entered.
     */
    private maxLength: number = 4;

    /**
     * Identifier for the specific lock this keypad is associated with.
     */
    private lockId: string = "";

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });

        const lockIdAttribute: string | null = this.getAttribute("lock-id");
        if (lockIdAttribute) {
            this.lockId = lockIdAttribute;
        }

        this.render();
    }

    /**
     * Renders the component's HTML structure into the shadow DOM.
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderContent()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        this.shadowRoot.append(...elements);
        this.addEventListeners();
    }

    /**
     * Render the content element
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        return `
        <div class="display" id="codeDisplay">----</div>
            <div class="content">
                <div class="keypad">
                    ${Array.from({ length: 10 }, (_, i) => `
                        <button class="key" data-value="${i}">${i}</button>
                    `).join("")}
                    <button id="clear">CLEAR</button>
                    <button id="back">LEAVE</button>
                    <button id="enter">ENTER</button>
                </div>
            </div>
        `;
    }

    /**
     * Attaches event listeners to the keypad buttons.
     * Sets up click handlers for numeric keys and action buttons.
     * @private
     */
    private addEventListeners(): void {
        this.shadowRoot!.querySelectorAll(".key").forEach(button => {
            button.addEventListener("click", event => {
                const value: string = (event.target as HTMLButtonElement).dataset.value!;
                this.addDigit(value);
            });
        });

        this.shadowRoot!.querySelector("#clear")!.addEventListener("click", () => this.clearCode());

        this.shadowRoot!.querySelector("#enter")!.addEventListener("click", () => this.enterCode());

        this.shadowRoot!.querySelector("#back")!.addEventListener("click", () => this.goBack());
    }

    /**
     * Submits the entered code to the game service for validation.
     * Dispatches custom events to notify the game of the keypad interaction.
     * Resets the display after submission.
     * @private
     * @returns {Promise<void>} A promise that resolves after the code has been processed
     */
    private async enterCode(): Promise<void> {
        const keypadResponse: GameState | undefined = await this._gameRouteService.executeKeypad(this.lockId, this.enterdCode);
        console.log(keypadResponse);

        this.enterdCode = "";
        this.updateDisplay();
        this.render();

        window.dispatchEvent(new CustomEvent("handle-action", { detail: "use:keypad" }));
        window.dispatchEvent(new CustomEvent("toggle-element", { detail: ".keypad" }));
    }

    /**
     * Adds a digit to the current code entry if maximum length hasn't been reached.
     * Updates the display to reflect the new code.
     * @private
     * @param {string} digit - The numeric digit to add to the code
     */
    private addDigit(digit: string): void {
        if (this.enterdCode.length < this.maxLength) {
            this.enterdCode += digit;
            this.updateDisplay();
            console.log(this.enterdCode);
        }
    }

    /**
     * Clears the current code entry and resets the display.
     */
    private clearCode(): void {
        this.enterdCode = "";
        this.updateDisplay();
    }

    /**
     * Exits the keypad interface by dispatching a custom event.
     */
    private goBack(): void {
        window.dispatchEvent(new CustomEvent("handle-action", { detail: ".keypad" }));
    }

    /**
     * Updates the code display with the current entered digits.
     * Pads the display with dashes to maintain consistent length visualization.
     */
    private updateDisplay(): void {
        const display: Element | null = this.shadowRoot!.querySelector("#codeDisplay");
        if (display) {
            display.textContent = this.enterdCode.padEnd(this.maxLength, "-");
        }
    }
}
